import { TFunction } from "@impero-com/common/utils/i18n";
import { TextToolState } from "@impero-com/ui";
import moment from "moment-timezone";

import { getCurrentLanguage } from "utils/locale";

import { ControlActivityTableRow } from "../types";

/** Calendar day, formatted as `YYYY-MM-DD`. */
export type CalendarDay = string;

export const CALENDAR_DAY_FORMAT = "YYYY-MM-DD";
/** Long day format used in the modal copy, e.g. "11 Sep 2026". */
const DAY_LONG_FORMAT = "D MMM YYYY";
/** Short day format for a range start that shares the end's year, e.g. "24 Jul". */
const DAY_SHORT_FORMAT = "D MMM";

/**
 * An activity can take a new deadline only while it is still running:
 * completed and archived activities are excluded.
 */
function isExtendable(row: ControlActivityTableRow): boolean {
    return (
        !row.archived &&
        row.workflowStatus !== "Completed" &&
        row.workflowStatus !== "CompletedAfterDueDate"
    );
}

export function computeExtendDueDateToolState(
    t: TFunction,
    rows: ControlActivityTableRow[]
): TextToolState {
    if (rows.length === 0) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_NoSelection"),
        };
    }
    if (rows.some((row) => !isExtendable(row))) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_ExtendDueDate_Tooltip_Ineligible"),
        };
    }
    if (rows.some((row) => !row.canEditProgram)) {
        return {
            type: "DISABLED",
            reason: t(
                "ControlActivitiesList_ExtendDueDate_Tooltip_NoPermission"
            ),
        };
    }
    return { type: "ENABLED" };
}

export interface SelectionDueDates {
    /** Earliest final due date across the selection, as an instant. */
    earliest: string;
    /** Latest final due date across the selection, as an instant. */
    latest: string;
}

export function computeSelectionDueDates(
    rows: ControlActivityTableRow[]
): SelectionDueDates | null {
    if (rows.length === 0) {
        return null;
    }
    const dueDates = rows.map((row) => moment(row.dueDate));
    return {
        earliest: moment.min(dueDates).toISOString(),
        latest: moment.max(dueDates).toISOString(),
    };
}

/** The calendar day an instant falls on in the given timezone. */
export function dayInTimezone(
    instant: string,
    timezoneName: string
): CalendarDay {
    return moment.tz(instant, timezoneName).format(CALENDAR_DAY_FORMAT);
}

/**
 * The first day the picker allows: the day after the latest due date in the
 * selection, so the new deadline is always strictly later than every current one.
 */
export function minSelectableDay(
    latestDueDate: string,
    timezoneName: string
): CalendarDay {
    return moment(
        dayInTimezone(latestDueDate, timezoneName),
        CALENDAR_DAY_FORMAT
    )
        .add(1, "day")
        .format(CALENDAR_DAY_FORMAT);
}

/** A calendar day in the modal's long copy format, e.g. "11 Sep 2026". */
export function formatDayLong(day: CalendarDay): string {
    // Month names come out in the user's language
    return moment(day, CALENDAR_DAY_FORMAT)
        .locale(getCurrentLanguage())
        .format(DAY_LONG_FORMAT);
}

export type DueDateSummary =
    | { type: "single"; date: string }
    | { type: "range"; earliest: string; latest: string };

/**
 * The "Current due dates" line under the selection count.
 */
export function summarizeDueDates(
    dueDates: SelectionDueDates,
    timezoneName: string
): DueDateSummary {
    const earliestDay = dayInTimezone(dueDates.earliest, timezoneName);
    const latestDay = dayInTimezone(dueDates.latest, timezoneName);
    if (earliestDay === latestDay) {
        return { type: "single", date: formatDayLong(earliestDay) };
    }
    const sameYear = earliestDay.slice(0, 4) === latestDay.slice(0, 4);
    const earliest = moment(earliestDay, CALENDAR_DAY_FORMAT)
        .locale(getCurrentLanguage())
        .format(sameYear ? DAY_SHORT_FORMAT : DAY_LONG_FORMAT);
    return { type: "range", earliest, latest: formatDayLong(latestDay) };
}

/**
 * The instant the picked calendar day stands for: 23:59 that day in the
 * timezone of the user performing the extension.
 */
export function newDueDateInstant(
    day: CalendarDay,
    timezoneName: string
): string {
    return moment
        .tz(`${day} 23:59`, `${CALENDAR_DAY_FORMAT} HH:mm`, timezoneName)
        .toISOString();
}

/** The timezone's abbreviation on the given day, e.g. "CEST". */
export function timezoneAbbreviation(
    day: CalendarDay | null,
    timezoneName: string
): string {
    const at = day
        ? moment.tz(day, CALENDAR_DAY_FORMAT, timezoneName)
        : moment.tz(timezoneName);
    return at.format("z");
}
